<template>
  <teleport to="body">
    <transition name="notification-backdrop">
      <div
        v-if="visible"
        class="notification-panel-backdrop"
        aria-hidden="true"
        @click="$emit('close')"
      ></div>
    </transition>

    <transition name="notification-panel">
      <aside
        v-if="visible"
        class="notification-panel"
        :class="{ 'notification-panel-dark': isDarkMode }"
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
      >
        <header class="notification-panel-header">
          <h2 class="notification-panel-title">Notifications</h2>
          <button
            type="button"
            class="notification-panel-close"
            aria-label="Close notifications"
            @click="$emit('close')"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </header>

        <div class="notification-panel-body">
          <div v-if="loading" class="notification-panel-loading">Loading notifications...</div>
          <template v-else-if="groupedNotifications.length">
            <section
              v-for="group in groupedNotifications"
              :key="group.label"
              class="notification-group"
            >
              <h3 class="notification-group-label">{{ group.label }}</h3>
              <ul class="notification-group-list">
                <li
                  v-for="item in group.items"
                  :key="item.id"
                  class="notification-item"
                  :class="{
                    'notification-item--unread': !item.isRead,
                    'notification-item--clickable': !item.isRead && patchingId !== item.id,
                    'notification-item--patching': patchingId === item.id
                  }"
                  @click="onNotificationClick(item)"
                >
                  <span class="notification-item-icon" aria-hidden="true">
                    <font-awesome-icon :icon="['fas', 'bell']" />
                  </span>
                  <div class="notification-item-content">
                    <p class="notification-item-type">{{ item.title }}</p>
                    <p v-if="item.body" class="notification-item-body">{{ item.body }}</p>
                  </div>
                </li>
              </ul>
            </section>
          </template>
          <p v-else-if="!loading" class="notification-panel-empty">No notifications yet.</p>
        </div>
      </aside>
    </transition>
  </teleport>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { groupNotifications } from '@/helper/notificationHelper';
import resolveNotificationRoute from '@/helper/dishaChatNotificationUtils';

export default {
  name: 'NotificationPanel',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    notifications: {
      type: Array,
      default: () => []
    },
    loading: Boolean,
    isDarkMode: Boolean
  },
  emits: ['close'],
  computed: {
    ...mapState('notification', ['patchingId']),
    groupedNotifications() {
      return groupNotifications(this.notifications);
    }
  },
  methods: {
    ...mapActions('notification', ['patchOne']),
    onNotificationClick(item) {
      if (!item) return;

      const route = resolveNotificationRoute(item);
      if (route) {
        if (!item.isRead && this.patchingId !== item.id) {
          this.patchOne({ item, type: 'read' });
        }
        this.$emit('close');
        this.$router.push(route).catch(() => {});
        return;
      }

      if (item.isRead || this.patchingId === item.id) {
        return;
      }
      this.patchOne({ item, type: 'read' });
    }
  }
};
</script>

<style scoped>
.notification-panel-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1045;
  background: rgba(15, 23, 42, 0.35);
}

.notification-panel {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  width: min(420px, 100vw);
  height: 100vh;
  background: #f8fafc;
  box-shadow: -8px 0 32px rgba(15, 23, 42, 0.12);
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
}

.notification-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 20px 20px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e8edf4;
}

.notification-panel-title {
  margin: 0;
  color: #19243b;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.notification-panel-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #6d788a;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.notification-panel-close:hover,
.notification-panel-close:focus {
  background: #eef2f7;
  color: #19243b;
  outline: none;
}

.notification-panel-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 8px 16px 24px;
}

.notification-group + .notification-group {
  margin-top: 8px;
}

.notification-group-label {
  margin: 0 0 10px;
  padding: 0 4px;
  color: #f28a3e;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.notification-group-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.notification-item--clickable {
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.notification-item--clickable:hover {
  border-color: #dfe6f0;
  background: #fcfdfe;
}

.notification-item--patching {
  opacity: 0.7;
  pointer-events: none;
}

.notification-group-list .notification-item + .notification-item {
  margin-top: 10px;
}

.notification-item-icon {
  flex-shrink: 0;
  width: 20px;
  margin-top: 2px;
  color: #9aa3b2;
  font-size: 18px;
  line-height: 1;
}

.notification-item-content {
  min-width: 0;
  flex: 1 1 auto;
}

.notification-item-type {
  margin: 0 0 4px;
  color: #f28a3e;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
}

.notification-item-body {
  margin: 0;
  color: #19243b;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
}

.notification-item--unread .notification-item-icon {
  color: #f28a3e;
}

/* Unread */
.notification-item--unread .notification-item-type {
  color: #f28a3e;
}

.notification-item--unread .notification-item-body {
  color: #19243b;
  font-weight: 700;
}

/* Read */
.notification-item:not(.notification-item--unread) .notification-item-type {
  color: #9aa3b2;
}

.notification-item:not(.notification-item--unread) .notification-item-body {
  color: #4b5563;
  font-weight: 500;
}

.notification-panel-empty {
  margin: 32px 0 0;
  color: #6d788a;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.notification-panel-loading {
  margin: 32px 0 0;
  color: #6d788a;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.notification-panel-dark {
  background: #1a2233;
  color: #e8edf4;
}

.notification-panel-dark .notification-item-body {
  color: #f3f6fb;
}

.notification-panel-dark .notification-item:not(.notification-item--unread) .notification-item-body {
  color: #c5cedb;
}

.notification-panel-dark .notification-panel-header {
  background: #1a2233;
  border-bottom-color: #2a3448;
}

.notification-panel-dark .notification-panel-title {
  color: #f3f6fb;
}

.notification-panel-dark .notification-panel-close {
  color: #9aa3b2;
}

.notification-panel-dark .notification-panel-close:hover,
.notification-panel-dark .notification-panel-close:focus {
  background: #243044;
  color: #f3f6fb;
}

.notification-panel-dark .notification-item {
  background: #243044;
  border-color: #2f3b52;
}

.notification-panel-dark .notification-item--clickable:hover {
  border-color: #3a465c;
  background: #28364c;
}

.notification-panel-dark .notification-item:not(.notification-item--unread) .notification-item-type {
  color: #8b95a8;
}

.notification-backdrop-enter-active,
.notification-backdrop-leave-active,
.notification-panel-enter-active,
.notification-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.25s ease;
}

.notification-backdrop-enter-from,
.notification-backdrop-leave-to {
  opacity: 0;
}

.notification-panel-enter-from,
.notification-panel-leave-to {
  transform: translateX(100%);
}

@media (max-width: 767.98px) {
  .notification-panel {
    width: 100vw;
  }
}
</style>
